import { FaPause } from "@react-icons/all-files/fa/FaPause";
import { FaPlay } from "@react-icons/all-files/fa/FaPlay";
import { Midi } from "@tonejs/midi";
import { Piano as TonePiano } from "@tonejs/piano";
import { theory } from "etude";
import React, {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Piano as EtudePiano } from "react-etude-piano";
import "./App.css";
import {
  clearIntermediateContext,
  createContext,
  setContextPaused,
  skipContext,
  stepContext,
} from "./MidiUtils";

const { Pitch } = theory;

function App() {
  const [piano, setPiano] = useState();
  const [midi, setMidi] = useState();
  const [fileName, setFileName] = useState(null);
  const [context, setContext] = useState(createContext(null));
  const [volume, setVolume] = useState(1);
  // keys the user is currently holding down, keyed by program number
  const [manualNotes, setManualNotes] = useState(() => new Map());
  const viewportRef = useRef(null);

  const pressPitch = useCallback(
    (pitch) => {
      if (piano == null) {
        return;
      }
      piano.keyDown({ midi: pitch.getProgramNumber() });
      setManualNotes((prev) => {
        const next = new Map(prev);
        next.set(pitch.getProgramNumber(), pitch);
        return next;
      });
    },
    [piano]
  );

  const releasePitch = useCallback(
    (pitch) => {
      if (piano == null) {
        return;
      }
      piano.keyUp({ midi: pitch.getProgramNumber() });
      setManualNotes((prev) => {
        if (!prev.has(pitch.getProgramNumber())) {
          return prev;
        }
        const next = new Map(prev);
        next.delete(pitch.getProgramNumber());
        return next;
      });
    },
    [piano]
  );

  // Safety net: release any held keys when the pointer lifts anywhere, so a
  // mouseup that lands off a key can't leave a note stuck down and lit.
  useEffect(() => {
    function releaseAll() {
      setManualNotes((prev) => {
        if (prev.size === 0) {
          return prev;
        }
        if (piano != null) {
          prev.forEach((pitch) =>
            piano.keyUp({ midi: pitch.getProgramNumber() })
          );
        }
        return new Map();
      });
    }
    window.addEventListener("mouseup", releaseAll);
    window.addEventListener("touchend", releaseAll);
    return () => {
      window.removeEventListener("mouseup", releaseAll);
      window.removeEventListener("touchend", releaseAll);
    };
  }, [piano]);

  const Piano = useMemo(() => {
    return lazy(async () => {
      if (piano == null) {
        const piano = await getPiano();
        setPiano(piano);
      }
      return { default: EtudePiano };
    });
  }, [piano]);

  async function getPiano() {
    const piano = new TonePiano({ maxPolyphony: Infinity });
    piano.toDestination();
    await piano.load();

    return piano;
  }

  useMemo(() => {
    if (midi == null) {
      return;
    }

    setContext(createContext(midi));
  }, [midi]);

  useEffect(() => {
    if (piano == null) {
      return;
    }

    return stepContext(
      context,
      (key) => piano.keyDown({ ...key, velocity: key.velocity * volume }),
      (key) => piano.keyUp({ ...key, velocity: key.velocity * volume }),
      piano.pedalDown.bind(piano),
      piano.pedalUp.bind(piano),
      setContext
    );
  }, [piano, context, volume]);

  // The keyboard keeps a fixed, comfortable size. When it's wider than the
  // screen (laptops, phones), start the view centered so middle C is in reach —
  // from there you can scroll or pinch-zoom to play any octave.
  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    if (viewport == null) {
      return;
    }
    const overflow = viewport.scrollWidth - viewport.clientWidth;
    if (overflow > 0) {
      viewport.scrollLeft = overflow / 2;
    }
  }, [piano]);

  function renderHeader() {
    return (
      <header className="masthead">
        <p className="eyebrow">The Self-Playing Piano</p>
        <h1 className="nameplate">Pianola</h1>
        <p className="tagline">
          A player piano for your MIDI files, crafted by{" "}
          <a
            href="https://andrewhan.dev"
            target="_blank"
            rel="noopener noreferrer"
          >
            andrewhan
          </a>
        </p>
      </header>
    );
  }

  function renderFileInput() {
    async function handleUpload(e) {
      const file = e.target.files[0];
      if (file == null) {
        return;
      }
      setFileName(file.name);
      const reader = new FileReader();
      reader.onload = () => setMidi(new Midi(reader.result));
      reader.readAsArrayBuffer(file);
    }

    return (
      <section className="loader">
        <label className="loader-button">
          <input type="file" accept=".mid" onChange={handleUpload} />
          <span className="loader-icon" aria-hidden="true" />
          <span>{midi == null ? "Load a music roll" : "Change roll"}</span>
        </label>
        <p className="loader-hint">
          {fileName == null ? "Select a .mid file to begin" : fileName}
        </p>
      </section>
    );
  }

  function renderMediaControls() {
    if (midi == null) {
      return;
    }

    function handleScrubChange(e) {
      clearIntermediateContext(
        context,
        (key) => piano.keyUp({ ...key, velocity: key.velocity * volume }),
        piano.pedalUp.bind(piano),
        setContext
      );

      const scrubIndex = parseInt(e.target.value);
      skipContext(context, scrubIndex, setContext);
    }

    function handlePlaybackButton() {
      setContextPaused(context, !context.paused, setContext);
    }

    function handleVolumeChange(e) {
      const volume = parseFloat(e.target.value);
      setVolume(volume);
    }

    const total = Math.max(context.actions.length - 1, 1);
    const progress = Math.round((context.index / total) * 100);

    return (
      <section className="transport">
        <button
          className={`transport-play ${context.paused ? "is-paused" : "is-playing"}`}
          onClick={handlePlaybackButton}
          aria-label={context.paused ? "Play" : "Pause"}
        >
          {context.paused ? <FaPlay /> : <FaPause />}
        </button>
        <div className="transport-dials">
          <div className="dial">
            <div className="dial-head">
              <span className="dial-label">Position</span>
              <span className="dial-value">{progress}%</span>
            </div>
            <input
              type="range"
              className="slider slider-progress"
              value={context.index}
              min={0}
              max={context.actions.length - 1}
              onChange={handleScrubChange}
            />
          </div>
          <div className="dial">
            <div className="dial-head">
              <span className="dial-label">Volume</span>
              <span className="dial-value">{Math.round(volume * 100)}%</span>
            </div>
            <input
              type="range"
              className="slider slider-volume"
              value={volume}
              min={0}
              max={1}
              step="any"
              onChange={handleVolumeChange}
            />
          </div>
        </div>
      </section>
    );
  }

  function renderPiano() {
    // Highlight both the notes the roll is playing (stored as name strings) and
    // the keys the user is holding (Pitch objects). The Piano only inspects the
    // first element's type to decide how to read the whole array, so keep it
    // homogeneous — convert the playback names to Pitch objects too. Mixing the
    // two types makes the library call Pitch.fromString on a Pitch and throw.
    const highlight = [
      ...Array.from(context.pressedNotes, (name) =>
        Pitch.fromString(name).orElse(null)
      ).filter(Boolean),
      ...manualNotes.values(),
    ];

    return (
      <section className="instrument">
        <div className="cabinet-lid">
          <span className="cabinet-brand">Pianola</span>
          <div className={`sustain-lamp ${context.sustain ? "is-lit" : ""}`}>
            <span className="lamp" aria-hidden="true" />
            Sustain
          </div>
        </div>
        <div className="keyboard">
          <span className="cheek cheek-left" aria-hidden="true" />
          <div className="keybed-column">
            <div className="felt-strip" />
            <div className="keybed-viewport" ref={viewportRef}>
              <div className="keybed">
                <Piano
                  start="A0"
                  end="C8"
                  onKeyPress={pressPitch}
                  onKeyRelease={releasePitch}
                  highlight={highlight}
                />
              </div>
            </div>
          </div>
          <span className="cheek cheek-right" aria-hidden="true" />
        </div>
      </section>
    );
  }

  return (
    <div className="stage">
      <div className="stage-glow" aria-hidden="true" />
      <Suspense
        fallback={
          <div className="loading">
            <span className="loading-dot" />
            <span className="loading-dot" />
            <span className="loading-dot" />
          </div>
        }
      >
        <div className="console">
          {renderHeader()}
          {renderFileInput()}
          {renderMediaControls()}
        </div>
        {renderPiano()}
      </Suspense>
    </div>
  );
}

export default App;
